import { useMemo } from 'react';

import { Form, Select } from 'antd';
import { isEmpty } from 'lodash';

import { WrapperSelect } from 'common/components';

import {
  HeaderGroupElement,
  GroupElementWrapper,
  FormItemList,
  FormItemText,
  FormItemBrick,
  FormItemBhtsCode,
  FormItemNumber,
  FormItemDatetime,
  FormItemContainer,
  FormItemBoolean,
  FormItemTextArea,
  FormItemIsFreeText,
  FormItemSubCategory,
  FormItemClass,
  FormItemCategory,
  FormItemDecimalNumber,
} from '../../shared/components';

import FormSingleNestedValue from './FormSingleNestedValue';
import FormMultipleNestedValue from './FormMultipleNestedValue';

import {
  useProductDetail,
  useProductDetailDispatch,
  productDetailActionTypes,
} from './ProductDetailContext';

import { useScrollToFoundModule } from '../../shared/hooks';

import {
  checkAllergenModuleName,
  checkAllergenProperty,
  getChangedField,
} from './allergenUtils';

import {
  findProductPropertyEnums,
  checkTypeOfFormNested,
  getKeyNestedObj,
  checkSpecialField,
} from './utils';
import { filterModuleProperties } from './filterModuleUtils';
import sortByKey from 'utils/sortByKey';

import { NUMERIC_TYPE } from 'static/Constants';
import {
  getRequiredRule,
  LIST_PROPERTY_TYPE_TEXTAREA,
} from 'pages/product-full-view/constants';

import {
  ACCESSORIES_GUN_CLEANING_SIZE,
  OPTICS_NIGHT_VISION_EYE_RELIEF_IN,
  OPTICS_NIGHT_VISION_MAGNIFICATION_DIGITAL_DIGITAL_ZOOM,
  OPTICS_NIGHT_VISION_MAGNIFICATION_DIGITAL_OPTICAL,
  OPTICS_NIGHT_VISION_SENSOR_RESOLUTION_HEIGHT,
  OPTICS_NIGHT_VISION_SENSOR_RESOLUTION_WIDTH,
  OPTICS_NIGHT_VISION_TOTAL_EFFECTIVE_MAGNIFICATION,
  OPTICS_OBSERVATION_WEIGHT,
  OPTICS_OPTICS_ACCESSORIES_HEIGHT,
  OPTICS_RIFLE_ELEVATION_TURRET_TRAVEL,
  OPTICS_RIFLE_ELEVATION_TURRET_TRAVEL_POSTFIX,
  OPTICS_RIFLE_EYE_RELIEF_IN,
  OPTICS_RIFLE_FOV_MAX_MAGNIFICATION,
  OPTICS_RIFLE_FOV_MIN_MAGNIFICATION,
  OPTICS_RIFLE_LENGTH,
  OPTICS_RIFLE_WEIGHT,
  OPTICS_RIFLE_WINDAGE_TURRET_TRAVEL,
  OPTICS_RIFLE_WINDAGE_TURRET_TRAVEL_POSTFIX,
  OPTICS_THERMAL_EYE_RELIEF_IN,
  OPTICS_THERMAL_HEIGHT,
  OPTICS_THERMAL_LENGTH,
  OPTICS_THERMAL_MAGNIFICATION_DIGITAL_DIGITAL_ZOOM,
  OPTICS_THERMAL_MAGNIFICATION_DIGITAL_OPTICAL,
  OPTICS_THERMAL_TOTAL_EFFECTIVE_MAGNIFICATION,
  OPTICS_THERMAL_WIDTH,
  getTextLimitCharacter,
} from 'pages/product-full-view/components/product-new-detail-view/constants';
import {
  FormItemBlackPowder,
  FormItemCaliberGauge,
} from 'pages/product-full-view/components/product-new-detail-view/components/FormItemAttributes';
import {
  FormItemAccessoriesOptics,
  FormItemOpticReady,
} from 'pages/product-full-view/components/product-new-detail-view/components/FormItemAccessories';
import { FormItemSdsSheetRequired } from 'pages/product-full-view/components/product-new-detail-view/components/FormItemCompliance';
import {
  FormItemBulletType,
  FormItemGrain,
  FormItemShotSize,
  FormItemShotWeight,
} from 'pages/product-full-view/components/product-new-detail-view/components/FormItemConstructionAmmunitionShotshell';
import {
  FormItemShortBarrelRifleBarrelLength,
  FormItemShortBarrelShotgunBarrelLength,
} from 'pages/product-full-view/components/product-new-detail-view/components/FormItemNFAFirearms';
import {
  FormItemElevationTurretTravel,
  FormItemEyeRelief,
  FormItemFocusRange,
  FormItemHeight,
  FormItemLength,
  FormItemMagnification,
  FormItemMagnificationDigital,
  FormItemSensorResolution,
  FormItemSizeIn,
  FormItemWeight,
  FormItemWindageTurretTravel,
} from 'pages/product-full-view/components/product-new-detail-view/components/FormItemOpticsAttributes';

const ProductDetailEdit = ({
  isGroup,
  ixoneChangedFieldRef,
  moduleNameProperties,
  productModules = {},
  productEnums,
  isEditBasedModule,
  handleValidate,
}) => {
  const filteredModuleNameProperties = useMemo(
    () => filterModuleProperties(moduleNameProperties),
    [moduleNameProperties],
  );

  const { moduleProperties: listProperties = [], moduleGroupProperties = [] } =
    filteredModuleNameProperties;

  const {
    formInstance,
    advancedModule: { foundFieldFullPath: advancedFoundFieldFullPath },
    baseModule: { foundFieldFullPath: baseFoundFieldFullPath },
  } = useProductDetail();

  const dispatch = useProductDetailDispatch();

  useScrollToFoundModule({
    elementByClassName: document.getElementsByClassName(
      `product-detail-view__label-form-item--${
        isEditBasedModule ? baseFoundFieldFullPath : advancedFoundFieldFullPath
      }`,
    ),
  });

  const handleSetIXONEFieldChange = (changedFields) => {
    const value = changedFields?.[0]?.value ?? null;
    const indexField = changedFields?.[0]?.name;

    // Filter the components of the field path to contain only strings.
    // fieldPathWithoutNumbers will have a structure similar to fieldFullPath of the BE.
    const fieldPathWithoutNumbers = indexField?.filter(
      (path) => typeof path === 'string' || path instanceof String,
    );

    const moduleName = moduleNameProperties?.moduleName;

    const fieldFullPath = isEditBasedModule
      ? fieldPathWithoutNumbers?.join('.')
      : moduleName + '.' + fieldPathWithoutNumbers?.join('.');

    // The customer request specified by ticket ID 7999.
    const specialModule = checkSpecialField(fieldFullPath);
    if (specialModule) return;

    // Assign the value and key of the changed field to an object
    ixoneChangedFieldRef.current[indexField?.join('.')] = {
      value,
      key: indexField,
      fieldFullPath,
    };
  };

  const handleDeleteFieldForm = ({ fieldFormValues, fieldPath }) => {
    const pathList = getKeyNestedObj(fieldFormValues, fieldPath?.join('.'));

    pathList?.forEach((path) => {
      const fieldPathWithoutNumbers = path?.split('.')?.filter((path) => {
        const indexOfPath = +path;
        return !(indexOfPath >= 0);
      });

      const moduleName = moduleNameProperties?.moduleName;

      const fieldFullPath = isEditBasedModule
        ? fieldPathWithoutNumbers?.join('.')
        : moduleName + '.' + fieldPathWithoutNumbers?.join('.');

      // Assign the value and key of the changed field to an object
      ixoneChangedFieldRef.current[path] = {
        value: null,
        key: path,
        fieldFullPath,
      };
    });
  };

  if (isGroup) {
    return (
      <div style={{ height: '80vh', padding: '6px 10px' }} className='scroller'>
        <Form
          form={formInstance}
          labelCol={{
            span: 6,
          }}
          className='product-detail__form'
          onFieldsChange={(changedFields, allFields) => {
            handleSetIXONEFieldChange(changedFields, allFields);
          }}
        >
          {moduleGroupProperties.map((groupItem) => {
            if (groupItem?.groupProperties?.length === 0) return null;

            return (
              <GroupElementWrapper
                key={groupItem.nameGroup}
                style={{ padding: 12, margin: '18px 0px' }}
              >
                <HeaderGroupElement
                  header={groupItem.nameGroup}
                  style={{ left: 0 }}
                />

                {groupItem.groupProperties.map((item) => {
                  return (
                    <FormElement
                      key={item.propertyName}
                      property={item}
                      productEnums={productEnums}
                      handleValidate={handleValidate}
                      deleteFieldForm={handleDeleteFieldForm}
                      productModules={productModules}
                    />
                  );
                })}
              </GroupElementWrapper>
            );
          })}
        </Form>
      </div>
    );
  }

  return (
    <div style={{ height: '80vh', padding: '6px 10px' }} className='scroller'>
      <Form
        form={formInstance}
        labelCol={{
          span: 6,
        }}
        className='product-detail__form'
        name='nested-messages'
        onFieldsChange={(changedFields) => {
          handleSetIXONEFieldChange(changedFields);

          if (checkAllergenModuleName(moduleNameProperties?.moduleName)) {
            // The 'allFields' value is an array, and we have updated one of the fields.
            // Therefore, we can retrieve the first field from the array.

            const indexField = changedFields?.[0]?.name;
            const changedField = getChangedField(indexField);

            // We have only validated each field (AllergenTypeCode, Region) in the Allergen section.
            // While deleting a field in the Allergen section will have an impact here.
            if (
              checkAllergenProperty(indexField) &&
              changedField !== 'Allergen'
            ) {
              const changedValueField = changedFields?.[0]?.value;

              dispatch({
                type: productDetailActionTypes.VALIDATE_VALUE_ALLERGEN_FIELD,
                payload: {
                  formInstance,
                  indexField,
                  changedValueField,
                },
              });
            }
          }
          return;
        }}
      >
        {listProperties.map((property) => {
          return (
            <FormElement
              key={property.propertyName}
              property={property}
              productEnums={productEnums}
              handleValidate={handleValidate}
              deleteFieldForm={handleDeleteFieldForm}
              productModules={productModules}
            />
          );
        })}
      </Form>
    </div>
  );
};
/*
  - Single nested value: childProperties.length > 0 && isArray = false;
    * A value of property is {{...}}


  - Multiple nested values: childProperties.length > 0 && isArray = true;
    * A value of property is [{...}, {...}]

*/

const FormElement = ({
  property,
  productEnums,
  handleValidate,
  deleteFieldForm,
  productModules,
}) => {
  const {
    childProperties,
    isArray,
    propertyType,
    propertyName,
    propertyDisplayName,
    fieldFullPath,
    listName,
    isFreeText,
  } = property;

  if (fieldFullPath.includes('SdsDataSheetsLinkId')) {
    // Never rendered here, regardless of listName/propertyType. FormItemCompliance.js's
    // SdsSheetRequiredControl already registers a hidden Form.Item for this
    // name so its value is still picked up by validateFields()/getFieldsValue()
    // on save.
    return null;
  }

  const { isSingleNestedValue, isMultipleNestedValue } = checkTypeOfFormNested(
    childProperties,
    isArray,
  );

  if (isSingleNestedValue) {
    if (
      fieldFullPath.includes('AccessoriesOptics') &&
      fieldFullPath !== 'Accessories.AccessoriesOptics'
    ) {
      return (
        <FormItemAccessoriesOptics
          productModules={productModules}
          name={property.propertyName}
          property={property}
          productEnums={productEnums}
          parentNestedIndex={[propertyName]}
          handleValidate={handleValidate}
          deleteFieldForm={deleteFieldForm}
        />
      );
    }
    return (
      <FormSingleNestedValue
        productModules={productModules}
        name={property.propertyName}
        property={property}
        productEnums={productEnums}
        parentNestedIndex={[propertyName]}
        handleValidate={handleValidate}
        deleteFieldForm={deleteFieldForm}
      />
    );
  }

  if (isMultipleNestedValue) {
    return (
      <FormMultipleNestedValue
        productModules={productModules}
        properties={childProperties}
        isArray={isArray}
        name={propertyName}
        label={propertyDisplayName}
        property={property}
        productEnums={productEnums}
        parentNestedIndex={[]}
        handleValidate={handleValidate}
        deleteFieldForm={deleteFieldForm}
      />
    );
  } else {
    if (listName) {
      const propertyEnums = findProductPropertyEnums(listName, productEnums);
      const requiredRule = getRequiredRule(property.fieldFullPath);

      if (isFreeText) {
        return (
          <FormItemIsFreeText
            property={property}
            key={propertyName}
            propertyDisplayName={propertyDisplayName}
            propertyEnums={propertyEnums}
          />
        );
      }

      if (property.fieldFullPath === 'Category') {
        return (
          <FormItemCategory
            property={property}
            key={propertyName}
            propertyName={propertyName}
            propertyDisplayName={propertyDisplayName}
            propertyEnums={propertyEnums}
          />
        );
      }

      if (property.fieldFullPath === 'SubCategory') {
        return (
          <FormItemSubCategory
            property={property}
            key={propertyName}
            propertyName={propertyName}
            propertyDisplayName={propertyDisplayName}
            propertyEnums={propertyEnums}
          />
        );
      }

      if (property.fieldFullPath === 'Class') {
        return (
          <FormItemClass
            property={property}
            key={propertyName}
            propertyName={propertyName}
            propertyDisplayName={propertyDisplayName}
            propertyEnums={propertyEnums}
          />
        );
      }

      if (property.fieldFullPath.includes('BlackPowderCaliberGauge')) {
        return (
          <FormItemBlackPowder
            property={property}
            propertyName={propertyName}
            propertyDisplayName={propertyDisplayName}
            propertyEnums={propertyEnums}
          />
        );
      }

      if (property.fieldFullPath === 'Attributes.CaliberGauge') {
        return (
          <FormItemCaliberGauge
            property={property}
            propertyName={propertyName}
            propertyDisplayName={propertyDisplayName}
            propertyEnums={propertyEnums}
          />
        );
      }

      if (
        property.fieldFullPath ===
        'ConstructionAmmunitionShotshell.SlugIfYesBulletType'
      ) {
        return (
          <FormItemBulletType
            property={property}
            propertyName={propertyName}
            propertyDisplayName={propertyDisplayName}
            propertyEnums={propertyEnums}
          />
        );
      }

      if (
        property.fieldFullPath ===
        'ConstructionAmmunitionShotshell.SlugIfNoShotSize'
      ) {
        return (
          <FormItemShotSize
            property={property}
            propertyName={propertyName}
            propertyDisplayName={propertyDisplayName}
            propertyEnums={propertyEnums}
          />
        );
      }

      if (
        property.fieldFullPath ===
          OPTICS_RIFLE_ELEVATION_TURRET_TRAVEL_POSTFIX ||
        property.fieldFullPath === OPTICS_RIFLE_WINDAGE_TURRET_TRAVEL_POSTFIX
      )
        return null;

      return (
        <FormItemContainer
          property={property}
          key={propertyName}
          propertyDisplayName={propertyDisplayName}
          rules={isEmpty(requiredRule) ? [] : [requiredRule]}
        >
          <WrapperSelect
            showSearch
            mode={isArray ? 'multiple' : ''}
            placeholder={`Please Select ${propertyDisplayName}`}
            filterOption={(input, option) =>
              option.children?.toLowerCase().indexOf(input?.toLowerCase()) >= 0
            }
          >
            {sortByKey(propertyEnums, 'enumDisplayName')?.map(
              (property, index) => (
                <Select.Option
                  value={property.enumDisplayName}
                  key={property.enumDisplayName + index}
                >
                  {property.enumDisplayName
                    ? property.enumDisplayName
                    : property.enumDescription}
                </Select.Option>
              ),
            )}
          </WrapperSelect>
        </FormItemContainer>
      );
    }

    if (isArray) {
      if (NUMERIC_TYPE.includes(property.propertyType)) {
        return (
          <FormItemList
            productModules={productModules}
            property={property}
            name={property.propertyName}
            className='product-detail__form-list--non-nested'
            variant='number'
          />
        );
      } else if (property.propertyType === 'datetime') {
        return (
          <FormItemList
            productModules={productModules}
            property={property}
            name={property.propertyName}
            className='product-detail__form-list--non-nested'
            variant='datetime'
          />
        );
      } else {
        return (
          <FormItemList
            productModules={productModules}
            property={property}
            name={property.propertyName}
            className='product-detail__form-list--non-nested'
            variant='text'
          />
        );
      }
    }

    if (propertyType === 'string') {
      if (property.fieldFullPath === 'BrickCode') {
        return (
          <FormItemBrick
            property={property}
            labelCol={{ span: 6 }}
            disabled={true}
          />
        );
      }
      if (property.fieldFullPath === 'BrickCodeName') {
        return (
          <FormItemText
            property={property}
            labelCol={{ span: 6 }}
            disabled={true}
          />
        );
      }

      if (property.propertyName === 'BhtsCode') {
        return (
          <FormItemBhtsCode
            property={property}
            labelCol={{ span: 6 }}
            rules={[getRequiredRule(property.fieldFullPath)]}
          />
        );
      }

      if (
        property.fieldFullPath === OPTICS_NIGHT_VISION_SENSOR_RESOLUTION_WIDTH
      ) {
        return <FormItemSensorResolution property={property} />;
      }

      if (
        property.fieldFullPath === OPTICS_NIGHT_VISION_SENSOR_RESOLUTION_HEIGHT
      )
        return null; // will cover in FormItemSensorResolution

      const textareaProperty =
        LIST_PROPERTY_TYPE_TEXTAREA[property.fieldFullPath];

      if (textareaProperty) {
        return (
          <FormItemTextArea
            property={property}
            style={{ marginBottom: '18px' }}
            rules={[getRequiredRule(property.fieldFullPath)]}
            {...textareaProperty}
          />
        );
      }

      return (
        <FormItemText
          property={property}
          labelCol={{ span: 6 }}
          rules={[getRequiredRule(property.fieldFullPath)]}
          maxLength={getTextLimitCharacter(property?.fieldFullPath)?.maxLength}
        />
      );
    }

    if (NUMERIC_TYPE.includes(propertyType)) {
      const requiredRule = getRequiredRule(property.fieldFullPath);

      if (
        property.fieldFullPath ===
        'ConstructionAmmunitionShotshell.SlugIfYesGrain'
      ) {
        return <FormItemGrain property={property} />;
      }

      if (
        property.fieldFullPath ===
        'ConstructionAmmunitionShotshell.SlugIfNoShotWeight'
      ) {
        return <FormItemShotWeight property={property} />;
      }

      if (
        property.fieldFullPath === 'NfaFirearms.ShortBarrelRifleBarrelLength'
      ) {
        return <FormItemShortBarrelRifleBarrelLength property={property} />;
      }

      if (
        property.fieldFullPath === 'NfaFirearms.ShortBarrelShotgunBarrelLength'
      ) {
        return <FormItemShortBarrelShotgunBarrelLength property={property} />;
      }

      if (property.fieldFullPath === OPTICS_RIFLE_ELEVATION_TURRET_TRAVEL) {
        return <FormItemElevationTurretTravel property={property} />;
      }

      if (property.fieldFullPath === OPTICS_RIFLE_WINDAGE_TURRET_TRAVEL) {
        return <FormItemWindageTurretTravel property={property} />;
      }

      if (
        property.fieldFullPath === OPTICS_RIFLE_FOV_MIN_MAGNIFICATION ||
        property.fieldFullPath === OPTICS_RIFLE_FOV_MAX_MAGNIFICATION
      ) {
        return <FormItemMagnification property={property} />;
      }

      if (property.fieldFullPath === OPTICS_RIFLE_LENGTH) {
        return <FormItemLength property={property} />;
      }

      if (
        property.fieldFullPath === OPTICS_RIFLE_WEIGHT ||
        property.fieldFullPath === OPTICS_OBSERVATION_WEIGHT
      ) {
        return <FormItemWeight property={property} />;
      }

      if (
        [
          OPTICS_NIGHT_VISION_MAGNIFICATION_DIGITAL_OPTICAL,
          OPTICS_NIGHT_VISION_MAGNIFICATION_DIGITAL_DIGITAL_ZOOM,
          OPTICS_NIGHT_VISION_TOTAL_EFFECTIVE_MAGNIFICATION,
          OPTICS_THERMAL_MAGNIFICATION_DIGITAL_OPTICAL,
          OPTICS_THERMAL_MAGNIFICATION_DIGITAL_DIGITAL_ZOOM,
          OPTICS_THERMAL_TOTAL_EFFECTIVE_MAGNIFICATION,
        ].includes(property.fieldFullPath)
      ) {
        return <FormItemMagnificationDigital property={property} />;
      }

      if (
        property.fieldFullPath === OPTICS_RIFLE_EYE_RELIEF_IN ||
        property.fieldFullPath === OPTICS_NIGHT_VISION_EYE_RELIEF_IN ||
        property.fieldFullPath === OPTICS_THERMAL_EYE_RELIEF_IN
      ) {
        return <FormItemEyeRelief property={property} />;
      }

      if (property.fieldFullPath === OPTICS_THERMAL_LENGTH) {
        return <FormItemSizeIn property={property} />;
      }

      /* Will be covered in FormItemSizeIn  */
      if (
        property.fieldFullPath === OPTICS_THERMAL_HEIGHT ||
        property.fieldFullPath === OPTICS_THERMAL_WIDTH
      )
        return null;

      if (
        property.fieldFullPath === 'AttributesOpticsObservation.CloseFocusRange'
      ) {
        return <FormItemFocusRange property={property} />;
      }

      if (property.fieldFullPath === OPTICS_OPTICS_ACCESSORIES_HEIGHT) {
        return <FormItemHeight property={property} />;
      }

      if (
        property.fieldFullPath === OPTICS_NIGHT_VISION_SENSOR_RESOLUTION_HEIGHT
      )
        return null; // will cover in FormItemSensorResolution

      if (property.fieldFullPath === ACCESSORIES_GUN_CLEANING_SIZE) {
        return (
          <FormItemDecimalNumber step={0.1} precision={1} property={property} />
        );
      }

      return (
        <FormItemNumber
          property={property}
          labelCol={{ span: 6 }}
          rules={isEmpty(requiredRule) ? [] : [requiredRule]}
        />
      );
    }

    if (propertyType === 'datetime') {
      return <FormItemDatetime property={property} labelCol={{ span: 6 }} />;
    }

    if (propertyType === 'boolean') {
      if (fieldFullPath.includes('OpticReady')) {
        return (
          <FormItemOpticReady
            property={property}
            parentModuleName={property?.fieldFullPath?.split('.')?.[0]}
          />
        );
      }
      if (fieldFullPath.includes('SdsDataSheets')) {
        return <FormItemSdsSheetRequired property={property} />;
      }
      return <FormItemBoolean property={property} labelCol={{ span: 6 }} />;
    }

    // Default display text input for catching bug from incomplete BE data
    return <FormItemText property={property} labelCol={{ span: 6 }} />;
  }
};

export default ProductDetailEdit;
